import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Container,
  Heading,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

const ENCRYPTED_PASSWORD = 'VHJAZDNfREBzaGIwQHJkJDIwMjY=';

const decryptPassword = (encrypted: string): string => {
  try {
    return atob(encrypted);
  } catch {
    return '';
  }
};

const SESSION_KEY = 'trading_access_granted';
const SESSION_DURATION = 12 * 60 * 60 * 1000;

interface TradingPasswordProtectionProps {
  children: React.ReactNode;
}

export const TradingPasswordProtection: React.FC<TradingPasswordProtectionProps> = ({ children }) => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const sessionData = localStorage.getItem(SESSION_KEY);
    if (sessionData) {
      try {
        const { timestamp } = JSON.parse(sessionData);
        const now = Date.now();
        if (now - timestamp < SESSION_DURATION) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const correctPassword = decryptPassword(ENCRYPTED_PASSWORD);
    
    if (password === correctPassword) {
      setIsAuthenticated(true);
      localStorage.setItem(SESSION_KEY, JSON.stringify({ timestamp: Date.now() }));
      toast({
        title: 'Access granted',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Incorrect password',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setPassword('');
    }
  };

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <Container maxW="md" centerContent py={20}>
      <Box
        p={8}
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
        bg="white"
        w="100%"
      >
        <VStack spacing={6} as="form" onSubmit={handleSubmit}>
          <Heading size="lg">Trading Access</Heading>
          <Text color="gray.600" textAlign="center">
            This section is password protected. Please enter the password to continue.
          </Text>
          
          <FormControl isRequired>
            <FormLabel>Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoFocus
              />
              <InputRightElement>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowPassword(!showPassword)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>
          
          <Button
            type="submit"
            colorScheme="blue"
            width="100%"
            isDisabled={!password}
          >
            Access Trading Dashboard
          </Button>
        </VStack>
      </Box>
    </Container>
  );
};
